import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Star, Target } from "lucide-react";
import {
  AssessmentManagerDashboardSummary,
} from "../../../../axios/quiz";
import { AssessmentScoreInline } from "../../assessment-score-inline";
import {
  completionBodyStyle,
  completionFootnoteStyle,
  completionLabelStyle,
  completionPercentStyle,
  completionRingCenterStyle,
  completionRingWrapStyle,
  overviewCardStyle,
  overviewCardTitleStyle,
  overviewMetricsGridStyle,
  teamMetricColumnStyle,
  teamMetricDividerStyle,
  teamMetricIconWrapStyle,
  teamMetricTextColumnStyle,
  teamMetricLabelStyle,
  teamMetricSubLabelStyle,
  teamMetricValueStyle,
  teamPerformanceBodyStyle,
} from "./styles";

const COMPLETION_RING_SIZE = 124;
const COMPLETION_RING_THICKNESS = 6;

interface AssessmentOverviewMetricsProps {
  summary: AssessmentManagerDashboardSummary;
  teamPerformance?: {
    average_score: number;
    max_score: number;
    average_bonus_score?: number;
    accuracy: number;
    average_mandatory_score?: number;
    average_bonus_question_score?: number;
    average_user_score?: number;
    average_gamification_bonus_score?: number;
    average_contest_points?: number;
    mandatory_max_score?: number;
    bonus_question_max_score?: number;
  };
}

const CompletionDonut = ({ percent }: { percent: number }) => {
  const value = Math.min(100, Math.max(0, Math.round(percent)));

  return (
    <Box sx={completionRingWrapStyle}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={COMPLETION_RING_SIZE}
        thickness={COMPLETION_RING_THICKNESS}
        sx={{ position: "absolute", color: "#E5E7EB" }}
      />
      <CircularProgress
        variant="determinate"
        value={value}
        size={COMPLETION_RING_SIZE}
        thickness={COMPLETION_RING_THICKNESS}
        sx={{
          position: "absolute",
          color: "#0EA5E9",
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
          },
        }}
      />
      <Box sx={completionRingCenterStyle}>
        <Typography sx={completionPercentStyle}>{value}%</Typography>
        <Typography sx={completionLabelStyle}>completed</Typography>
      </Box>
    </Box>
  );
};

export const AssessmentOverviewMetrics = ({
  summary,
  teamPerformance,
}: AssessmentOverviewMetricsProps) => {
  const completionPercent =
    summary.completion_percent ??
    Math.round(
      (summary.completed / Math.max(1, summary.invited)) * 100,
    );
  const accuracy = Math.round(teamPerformance?.accuracy ?? 0);
  const averageUserScore =
    teamPerformance?.average_user_score ?? teamPerformance?.average_score ?? 0;
  const mandatoryMaxScore =
    teamPerformance?.mandatory_max_score ?? teamPerformance?.max_score ?? 0;
  const averageGamificationBonus =
    teamPerformance?.average_gamification_bonus_score ??
    teamPerformance?.average_bonus_score ??
    0;
  const averageMandatoryScore = teamPerformance?.average_mandatory_score;
  const averageBonusQuestionScore =
    teamPerformance?.average_bonus_question_score;

  return (
    <Box sx={overviewMetricsGridStyle}>
      <Box sx={overviewCardStyle}>
        <Typography sx={overviewCardTitleStyle}>Overall completion</Typography>
        <Box sx={completionBodyStyle}>
          <CompletionDonut percent={completionPercent} />
          <Typography sx={completionFootnoteStyle}>
            {summary.completed} of {summary.invited} participants
          </Typography>
        </Box>
      </Box>

      <Box sx={overviewCardStyle}>
        <Typography sx={overviewCardTitleStyle}>
          Average team performance
        </Typography>
        <Box sx={teamPerformanceBodyStyle}>
          <Box sx={teamMetricColumnStyle}>
            <Box sx={teamMetricIconWrapStyle}>
              <Target size={18} />
            </Box>
            <Box sx={teamMetricTextColumnStyle}>
              <Typography sx={teamMetricLabelStyle}>Accuracy</Typography>
              <Typography sx={teamMetricValueStyle}>{accuracy}%</Typography>
              <Typography sx={teamMetricSubLabelStyle}>Team average</Typography>
            </Box>
          </Box>
          <Box sx={teamMetricDividerStyle} />
          <Box sx={teamMetricColumnStyle}>
            <Box sx={teamMetricIconWrapStyle}>
              <Star size={18} />
            </Box>
            <Box sx={teamMetricTextColumnStyle}>
              <Typography sx={teamMetricLabelStyle}>Average score</Typography>
              <Typography sx={teamMetricValueStyle}>
                <AssessmentScoreInline
                  score={averageUserScore + averageGamificationBonus}
                  maxScore={mandatoryMaxScore}
                  bonusScore={averageGamificationBonus}
                />
              </Typography>
              {typeof averageMandatoryScore === "number" ||
              typeof averageBonusQuestionScore === "number" ? (
                <Typography sx={teamMetricSubLabelStyle}>
                  Mandatory {Math.round(averageMandatoryScore ?? 0)}
                  {" · "}
                  Bonus Q {Math.round(averageBonusQuestionScore ?? 0)}
                </Typography>
              ) : (
                <Typography sx={teamMetricSubLabelStyle}>Team average</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentOverviewMetrics;
